import { PartnerAccount } from "@mezo/api"
import { createSlice } from "@reduxjs/toolkit"

export type Notification = "account-created" | "used-code" | null

export type ModalType =
  | "wallet-connect"
  | "deposit"
  | "boost"
  | "withdrawal"
  | "stbtc-manage"
  | "migrate-deposit"
  | "stack-matsnet-btc"
  | "store-item"
  | "taho-nft-store-item"
  | "taho-nft-store-item-success"
  | "store-feedback"
  | "store-item-success"
  | "error-modal-generic"
  | "error-modal-support"
  | "error-mats-claim"
  | "borrow-first"
  | "borrow-update"
  | "borrow-manage"
  | "borrow-repay"
  | "onboarding"
  | "add-liquidity"
  | "staking"
  | "stake-withdrawal"
  | "manage-stake"
  | "migrate-to-mainnet"
  | "bridge-asset"
  | null

type ModalOptions<CustomOptions = unknown> = {
  initialActiveDepositUuid?: string
  allowToChangeDeposit?: boolean
} & CustomOptions

type Partner = PartnerAccount | null

export type UIState = {
  hasLoadedAccountData: boolean
  hasAccess: boolean
  canSendTransactions: boolean
  notification: Notification
  openModal: ModalType
  modalOptions: ModalOptions
  modalOpenedAt: number | null
  partner: Partner
  hasLoadedPartnerData: boolean
}

const initialState: UIState = {
  hasLoadedAccountData: false,
  hasAccess: false,
  canSendTransactions: false,
  notification: null,
  openModal: null,
  modalOptions: { allowToChangeDeposit: false },
  modalOpenedAt: null,
  partner: null,
  hasLoadedPartnerData: false,
}

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    setHasLoadedAccountData: (
      immerState,
      { payload }: { payload: boolean },
    ) => {
      immerState.hasLoadedAccountData = payload
    },
    setHasAccess: (immerState, { payload }: { payload: boolean }) => {
      immerState.hasAccess = payload
    },
    setCanSendTransactions: (immerState, { payload }: { payload: boolean }) => {
      immerState.canSendTransactions = payload
    },
    setNotification: (immerState, { payload }: { payload: Notification }) => {
      immerState.notification = payload
    },
    setOpenModal: (
      immerState,
      { payload }: { payload: { type: ModalType; options?: ModalOptions } },
    ) => {
      immerState.openModal = payload.type
      immerState.modalOptions = {
        ...immerState.modalOptions,
        ...payload.options,
      }
      immerState.modalOpenedAt = Date.now()
    },
    closeOpenModal: (immerState) => {
      immerState.openModal = null
      immerState.modalOptions = initialState.modalOptions
      immerState.modalOpenedAt = null
    },
    setPartner: (immerState, { payload }: { payload: Partner }) => {
      immerState.partner = payload
    },
    setHasLoadedPartnerData: (
      immerState,
      { payload }: { payload: boolean },
    ) => {
      immerState.hasLoadedPartnerData = payload
    },
    resetUI: (immerState) => ({
      ...initialState,
      partner: immerState.partner,
      hasLoadedPartnerData: immerState.hasLoadedPartnerData,
    }),
  },
})

/**
 * Wrapper for setOpenModal reducer to allow us to call the function in
 * a following pattern: setOpenModal("type", { option1: "value" })
 */
export const setOpenModal = <Options>(
  type: ModalType,
  options?: ModalOptions<Options>,
) => uiSlice.actions.setOpenModal({ type, options })

export const {
  setHasAccess,
  setCanSendTransactions,
  setHasLoadedAccountData,
  setNotification,
  setPartner,
  setHasLoadedPartnerData,
  closeOpenModal,
  resetUI,
} = uiSlice.actions

export default uiSlice.reducer
